import { createAsyncThunk } from '@reduxjs/toolkit';
import { getOperatorWorkspaceRequest, getStockRequest, scanCodeRequest } from './operatorApi';
import { localError, readOperatorError } from './operatorErrors';
import { SCAN_GATE, cleanCode, scanGate } from './operatorScanGate';
import { LOOKUP_KINDS, OPERATOR_FLOW } from './operatorConstants';
import { offScreenMeta } from './operatorTaskScope';
import { selectCurrentOperatorWarehouseId } from './operatorUtils';

/**
 * Who the operator is and what each warehouse lets them do, with the size of
 * every queue. The whole workspace hangs from this answer, never from the
 * login's addons: those leave out the warehouses an operator reaches through
 * their admin.
 */
export const fetchOperatorContext = createAsyncThunk(
  'operator/fetchContext',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getOperatorWorkspaceRequest();
      return data;
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos cargar tu espacio de trabajo.'));
    }
  },
  { condition: (_, { getState }) => !getState().operator.loading }
);

/**
 * A lookup is no longer on screen once Limpiar or another warehouse dropped
 * it, or once the operator left Consulta: its answer says nothing, and leaves
 * the screen they are on alone. Only one dropped changes nothing in Consulta.
 */
const lookupMeta = (getState, requestId, code) => {
  const state = getState();
  const name = cleanCode(code);
  if (state.operator.lookup.requestId !== requestId) return { offScreenTask: name };
  return offScreenMeta(state, OPERATOR_FLOW.STOCK, null, name);
};

/**
 * Consulta: what a code is in the current warehouse.
 *
 * @param {{code: string, source: string}} read
 */
export const lookupCode = createAsyncThunk(
  'operator/lookupCode',
  async ({ code }, { getState, requestId, fulfillWithValue, rejectWithValue }) => {
    try {
      const warehouseId = selectCurrentOperatorWarehouseId(getState());
      const { data } = await scanCodeRequest(warehouseId, cleanCode(code), LOOKUP_KINDS);
      return fulfillWithValue(data, lookupMeta(getState, requestId, code));
    } catch (error) {
      return rejectWithValue(
        readOperatorError(error, 'No pudimos buscar ese código.'),
        lookupMeta(getState, requestId, code)
      );
    }
  },
  {
    condition: (read, { getState }) => {
      const { lookup } = getState().operator;
      return scanGate({ busy: lookup.loading, lastCode: lookup.lastCode }, read) === SCAN_GATE.ACCEPT;
    },
  }
);

/**
 * The scan gate of an order queue: busy only with its own scan, and the last
 * code only if it was its own. Another queue reads the same label afresh.
 *
 * @param {object} orderScan - `operator.orderScan`.
 * @param {string} flow
 * @returns {{busy: boolean, lastCode: string|null}}
 */
export const orderScanGate = (orderScan, flow) =>
  orderScan.flow === flow ? { busy: orderScan.finding, lastCode: orderScan.lastCode } : { busy: false, lastCode: null };

/** How many of the other codes in view an order queue asks for, after the one read finds no order. */
const ORDER_ALTERNATIVES = 2;

/**
 * The orders the scan finds for a code. A code it knows nothing of answers
 * 404: no order, and `unknown` keeps that answer. Any other failure throws.
 */
const ordersFor = async (warehouseId, code) => {
  try {
    const { data } = await scanCodeRequest(warehouseId, code, ['order']);
    return { orders: (data.results || []).filter((result) => result.kind === 'order') };
  } catch (error) {
    if (error?.response?.status === 404) return { orders: [], unknown: error };
    throw error;
  }
};

/** The first of `codes` the scan finds orders for, asked one after another; `tried`, those it found none for. */
const firstOrdersOf = async (warehouseId, [code, ...rest], tried = []) => {
  if (code === undefined) return { orders: null, tried };
  const { orders } = await ordersFor(warehouseId, code);
  if (orders.length > 0) return { orders, matched: code, tried };
  return firstOrdersOf(warehouseId, rest, [...tried, code]);
};

/**
 * The order a code scanned at a queue is — its reference, number or a
 * tracking number — and where it goes now (`next_flow`). The one that goes
 * to the queue's own flow comes first, when two share the code. A label
 * carries several codes: when the one read is no order, the others the camera
 * saw beside it are asked (`meta.matched` is the one that found it,
 * `meta.tried` the others asked). Once the queue is off screen — the operator
 * opened something else meanwhile — the answer says so in
 * `meta.offScreenTask`, and opens nothing.
 *
 * @param {{code: string, source: string, flow: 'picking'|'packing', alternatives?: string[]}} read
 */
export const findOrder = createAsyncThunk(
  'operator/findOrder',
  async ({ code, flow, alternatives = [] }, { getState, fulfillWithValue, rejectWithValue }) => {
    const value = cleanCode(code);
    const queueMeta = (extra) => ({ ...offScreenMeta(getState(), flow, null, value), ...extra });
    try {
      const warehouseId = selectCurrentOperatorWarehouseId(getState());
      const read = await ordersFor(warehouseId, value);
      const found =
        read.orders.length > 0
          ? { orders: read.orders, matched: value }
          : await firstOrdersOf(warehouseId, alternatives.slice(0, ORDER_ALTERNATIVES));
      if (!found.orders) {
        // What is said is about the code read, as when nothing else was in view.
        const meta = queueMeta({ tried: found.tried });
        return read.unknown
          ? rejectWithValue(readOperatorError(read.unknown, 'No pudimos buscar esa orden.'), meta)
          : rejectWithValue(localError(`${value} no es una orden de esta bodega.`), meta);
      }
      const order = found.orders.find((candidate) => candidate.next_flow === flow) || found.orders[0];
      return fulfillWithValue(order, queueMeta({ matched: found.matched }));
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos buscar esa orden.'), queueMeta());
    }
  },
  {
    condition: (read, { getState }) =>
      scanGate(orderScanGate(getState().operator.orderScan, read.flow), read) === SCAN_GATE.ACCEPT,
  }
);

/**
 * What Consulta can ask the stock of for a scan result: a product or a slot,
 * by id. Receipts and supplies have none.
 *
 * @param {{kind: string, product_id?: number, slot_id?: number}} result
 * @returns {{kind: 'product'|'slot', id: number}|null}
 */
export const stockTargetOf = (result) => {
  if (result.kind === 'product') return { kind: 'product', id: result.product_id };
  if (result.kind === 'slot') return { kind: 'slot', id: result.slot_id };
  return null;
};

/** A stock target's handle, `product:5` or `slot:9`: a product and a slot can share an id. */
export const stockTargetKey = (target) => `${target.kind}:${target.id}`;

const STOCK_PARAMS = { product: 'product_id', slot: 'slot_id' };

/**
 * Consulta: the stock of a result — what there is of a product here and
 * where, or what a slot holds.
 *
 * @param {{kind: 'product'|'slot', id: number}} target - From `stockTargetOf`.
 */
export const fetchStock = createAsyncThunk(
  'operator/fetchStock',
  async (target, { getState, rejectWithValue }) => {
    try {
      const warehouseId = selectCurrentOperatorWarehouseId(getState());
      const { data } = await getStockRequest(warehouseId, { [STOCK_PARAMS[target.kind]]: target.id });
      return data;
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos cargar el stock.'));
    }
  },
  {
    // A second tap on the same result waits for the first; another result's replaces it.
    condition: (target, { getState }) => {
      const { stock } = getState().operator.lookup;
      return !(stock.loading && stockTargetKey(stock.target) === stockTargetKey(target));
    },
  }
);
